import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AlertTriangle, HelpCircle, X } from 'lucide-react';
import { Button } from '@/design-system/components/Button/Button';

/**
 * In-app replacement for window.confirm() (audit D-006). The native dialog
 * blocks the renderer, looks nothing like DemoOps, and — decisive for a field
 * PWA — some installed-app display modes suppress it entirely, so the guarded
 * action silently never runs on a phone. This is the RemoveEmployeeDialog
 * pattern extracted into the design system behind a promise API:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Delete this cost?', tone: 'danger' }))) return;
 *
 * Cancel, Escape, backdrop click, and unmount all resolve false — the promise
 * always settles, so callers can await it unconditionally. Opening a second
 * dialog while one is up cancels the first.
 */
export interface ConfirmOptions {
  title: string;
  /** Optional supporting copy under the title (consequences, scope). */
  body?: string;
  /** Verb-first label, e.g. "Delete", "Approve". Defaults to "Confirm". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** danger = destructive red action; primary = regular commitment. */
  tone?: 'danger' | 'primary';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
}

interface Pending {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setPending((prev) => {
        // A second ask while one is open cancels the first — never strand a
        // caller on an unsettled promise.
        prev?.resolve(false);
        return { options, resolve };
      });
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    setPending((prev) => {
      prev?.resolve(value);
      return null;
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <ConfirmDialog
          options={pending.options}
          onConfirm={() => settle(true)}
          onCancel={() => settle(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

function ConfirmDialog({
  options,
  onConfirm,
  onCancel,
}: {
  options: ConfirmOptions;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const tone = options.tone ?? 'danger';
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Focus lands on Cancel: the dialog guards destructive actions, so the
  // no-op must be the key pressed by muscle memory (Enter/Space).
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const Icon = tone === 'danger' ? AlertTriangle : HelpCircle;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center"
      onClick={onCancel}
      data-testid="confirm-dialog-backdrop"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby={options.body ? 'confirm-dialog-body' : undefined}
        className="w-full max-w-md rounded-t-2xl bg-graphite-900 p-5 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          {/* A title (or body) naming a file can be one long word — a J: name
              (Docuvault review 2026-10-10). The heading beside Close and its
              words beside the icon are flex items, never narrower than that
              word unless min-w-0; break-words then breaks it. */}
          <h2
            id="confirm-dialog-title"
            className="flex min-w-0 items-center gap-2 text-lg font-semibold text-graphite-100"
          >
            <Icon
              className={`h-5 w-5 shrink-0 ${tone === 'danger' ? 'text-amber-400' : 'text-steel-400'}`}
              aria-hidden="true"
            />
            <span className="min-w-0 break-words">{options.title}</span>
          </h2>
          <button
            onClick={onCancel}
            aria-label="Close"
            className="text-graphite-400 hover:text-graphite-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {options.body && (
          <p id="confirm-dialog-body" className="break-words text-sm text-graphite-300">
            {options.body}
          </p>
        )}

        <div className="mt-4 flex flex-col gap-2 sm:flex-row-reverse">
          <Button
            variant={tone === 'danger' ? 'destructive' : 'primary'}
            className="sm:flex-1"
            onClick={onConfirm}
          >
            {options.confirmLabel ?? 'Confirm'}
          </Button>
          <Button ref={cancelRef} variant="ghost" onClick={onCancel}>
            {options.cancelLabel ?? 'Cancel'}
          </Button>
        </div>
      </div>
    </div>
  );
}
